import { FunctionComponent, KeyboardEvent, MouseEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ProgressSpinner } from "primereact/progressspinner";
import { classNames } from "primereact/utils";

import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "./ColumnActionIcon.scss";

import { TooltipWrapper } from "../Components/TooltipWrapper/TooltipWrapper";

export interface IDeleteIndexButtonProps {
  rowData: any;
  tooltipTranslationKey?: string;
  tooltip?: string;
  tooltipPosition?: "top" | "bottom" | "left" | "right";
  icon: IconDefinition;
  confirmAction?: () => Promise<boolean>;
  disabled?: boolean;
  className?: "default" | "warning" | "danger";
  executeAction: (rowData: any) => Promise<void> | void;
}

export const ColumnActionIcon: FunctionComponent<IDeleteIndexButtonProps> = (props: IDeleteIndexButtonProps) => {
  const { t } = useTranslation();

  const actionButtonRef = useRef<HTMLDivElement>(null);
  const [isExecuting, setIsExecuting] = useState(false);

  const handleClickActionButton = async (event: MouseEvent | KeyboardEvent) => {
    if (props.disabled || isExecuting) {
      return;
    }

    if (!props.confirmAction || (await props.confirmAction())) {
      setIsExecuting(true);

      await props.executeAction(props.rowData);

      setIsExecuting(false);
    }
  };

  return (
    <div className="column-action-container">
      <TooltipWrapper
        targetRef={actionButtonRef}
        position={props.tooltipPosition ?? "left"}
        content={props.tooltip ?? t(props.tooltipTranslationKey ?? "")}
        disabled={!props.tooltip?.length && !props.tooltipTranslationKey?.length}
      />

      <div
        className={classNames(`column-action ${props.className}`, {
          disabled: props.disabled,
        })}
        ref={actionButtonRef}
        onClick={handleClickActionButton}
        onKeyDown={event => event.key === "Enter" && handleClickActionButton(event)}
        tabIndex={0}
        title={props.tooltip ?? t(props.tooltipTranslationKey ?? "")}
        role="button"
      >
        {isExecuting ? (
          <ProgressSpinner className="small-inline-spinner" />
        ) : (
          <FontAwesomeIcon icon={props.icon} size="1x" />
        )}
      </div>
    </div>
  );
};
